import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Hls from 'hls.js';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Play, Pause, Heart, Share2, Copy, Check, ArrowLeft,
  Clock, Eye, ChevronRight, SkipBack, SkipForward, RotateCcw,
  Maximize, Minimize, X, Download, Loader2, User, Smartphone, Monitor,
} from 'lucide-react';
import { toast } from 'sonner';
import { XbotCoverOverlay } from '../components/XbotCoverOverlay';
import { SeoHead } from '../components/SeoHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClipView() {
  const { clipId } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const hlsRef = useRef(null);
  const [clip, setClip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [copied, setCopied] = useState(false);
  const [liked, setLiked] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [verticalMode, setVerticalMode] = useState(false);  // 9:16 toggle for IG/TikTok preview
  const [taggedAthlete, setTaggedAthlete] = useState(null);
  const endCheckRef = useRef(null);

  useEffect(() => {
    const fetchClip = async () => {
      try {
        const res = await fetch(`${API_URL}/api/vod/clips/${clipId}`);
        if (res.ok) {
          const data = await res.json();
          setClip(data);
          // Track view
          fetch(`${API_URL}/api/vod/clips/${clipId}/views`, { method: 'POST' }).catch(() => {});
          // Fetch tagged athlete if player_name exists
          if (data.player_name) {
            try {
              const athRes = await fetch(`${API_URL}/api/athletes?search=${encodeURIComponent(data.player_name)}&limit=1`);
              if (athRes.ok) {
                const athletes = await athRes.json();
                if (athletes.length > 0 && athletes[0].name?.toLowerCase() === data.player_name.toLowerCase()) {
                  setTaggedAthlete(athletes[0]);
                }
              }
            } catch { /* ignore */ }
          }
        }
      } catch (err) {
        console.error('Error fetching clip:', err);
      }
      setLoading(false);
    };
    fetchClip();

    return () => {
      if (hlsRef.current) hlsRef.current.destroy();
      if (endCheckRef.current) clearInterval(endCheckRef.current);
    };
  }, [clipId]);

  useEffect(() => {
    if (!clip || !videoRef.current) return;
    const video = videoRef.current;
    const src = clip.playback_url || clip.video_url;
    if (!src) return;

    if (src.includes('.m3u8') && Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true, lowLatencyMode: false });
      hls.loadSource(src);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.currentTime = clip.start_time;
      });
      hlsRef.current = hls;
    } else {
      video.src = src;
      video.addEventListener('loadedmetadata', () => {
        video.currentTime = clip.start_time;
      });
    }

    // Monitor playback to stop at clip end
    endCheckRef.current = setInterval(() => {
      if (video.currentTime >= clip.end_time) {
        video.pause();
        video.currentTime = clip.start_time;
        setPlaying(false);
      }
      setCurrentTime(video.currentTime);
    }, 200);
  }, [clip]);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (playing) {
      videoRef.current.pause();
    } else {
      if (videoRef.current.currentTime >= clip.end_time || videoRef.current.currentTime < clip.start_time) {
        videoRef.current.currentTime = clip.start_time;
      }
      videoRef.current.play();
    }
    setPlaying(!playing);
  };

  const skipBack = () => {
    if (!videoRef.current || !clip) return;
    const newTime = Math.max(clip.start_time, videoRef.current.currentTime - 5);
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const skipForward = () => {
    if (!videoRef.current || !clip) return;
    const newTime = Math.min(clip.end_time - 0.5, videoRef.current.currentTime + 5);
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const replayClip = () => {
    if (!videoRef.current || !clip) return;
    videoRef.current.currentTime = clip.start_time;
    videoRef.current.play();
    setPlaying(true);
  };

  // Fullscreen handling
  const toggleFullscreen = useCallback(() => {
    const elem = containerRef.current;
    if (!elem) return;

    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  }, []);

  const exitFullscreen = useCallback(() => {
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  }, []);

  const handleBack = () => {
    exitFullscreen();
    navigate(-1);
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement || !!document.webkitFullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const handleLike = () => {
    if (liked) return;
    fetch(`${API_URL}/api/vod/clips/${clipId}/like`, { method: 'POST' }).catch(() => {});
    setClip(prev => ({ ...prev, likes: (prev.likes || 0) + 1 }));
    setLiked(true);
  };

  const handleShare = async () => {
    const url = window.location.href;
    fetch(`${API_URL}/api/vod/clips/${clipId}/share`, { method: 'POST' }).catch(() => {});

    if (navigator.share) {
      try {
        await navigator.share({ title: clip.title, text: `${clip.title} - ${clip.home_team} vs ${clip.away_team}`, url });
        return;
      } catch (e) {
        if (e.name === 'AbortError') return;
      }
    }
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = async () => {
    if (!clip) return;
    setDownloading(true);

    // Fast path: MP4 already rendered
    if (clip.mp4_url) {
      window.open(clip.mp4_url, '_blank');
      setDownloading(false);
      toast.success('Download started');
      return;
    }

    // No server-side render available — fall back to copying the share link
    if (!clip.cf_clip_uid) {
      const info = `🎬 ${clip.title}\n📺 ${clip.home_team} vs ${clip.away_team}\n🔗 ${window.location.href}\n\nPowered by Rooam Sports Network`;
      try { await navigator.clipboard.writeText(info); } catch { /* ignore */ }
      toast.info('No downloadable MP4 yet — share link copied instead');
      setDownloading(false);
      return;
    }

    toast.info('Preparing your watermarked MP4… this can take 30–60s');

    try {
      // Kick off generation (idempotent)
      await fetch(`${API_URL}/api/vod/clips/${clipId}/enable-download`, {
        method: 'POST',
      });

      // Poll every 4s, up to 90s
      const start = Date.now();
      while (Date.now() - start < 90_000) {
        await new Promise((r) => setTimeout(r, 4000));
        const res = await fetch(`${API_URL}/api/vod/clips/${clipId}/download-status`);
        if (!res.ok) continue;
        const data = await res.json();
        if (data.status === 'ready' && data.mp4_url) {
          setClip((prev) => ({ ...prev, mp4_url: data.mp4_url, render_status: 'ready' }));
          window.open(data.mp4_url, '_blank');
          toast.success('Download ready');
          setDownloading(false);
          return;
        }
        if (data.status === 'unavailable') {
          toast.error('MP4 download unavailable for this clip');
          setDownloading(false);
          return;
        }
      }
      toast.message('Still rendering — try again in a moment');
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const formatTime = (s) => {
    const num = Number(s);
    if (!Number.isFinite(num)) return '0:00';
    const m = Math.floor(num / 60);
    const sec = Math.floor(num % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!clip) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center text-white">
        <h1 className="text-2xl font-bold mb-2">Clip not found</h1>
        <p className="text-zinc-500 mb-4">This clip may have been deleted.</p>
        <Button asChild variant="outline" className="border-zinc-700">
          <Link to="/"><ArrowLeft className="w-4 h-4 mr-2" /> Back to Home</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <SeoHead
        title={clip.title}
        description={`${clip.home_team || ''} vs ${clip.away_team || ''} — ${clip.duration || 0}s highlight clip from Rooam Sports Network.`}
        path={`/clip/${clipId}`}
        image={clip.thumbnail_url}
      />
      {/* Header with Back Button and Logo */}
      <div className="bg-black border-b border-zinc-900">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
            data-testid="clip-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Back</span>
          </button>
          
          {/* Rooam Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="/rooam-logo.png" 
              alt="Rooam Sports Network" 
              className="h-10 sm:h-12 w-auto"
            />
          </Link>
          
          <div className="w-16" /> {/* Spacer for centering */}
        </div>
      </div>

      {/* Video Player */}
      <div className="relative max-w-4xl mx-auto pt-4">
        <div
          ref={containerRef}
          className={`relative bg-zinc-950 ${
            isFullscreen
              ? 'fixed inset-0 z-50 max-w-none aspect-auto'
              : verticalMode
                ? 'aspect-[9/16] max-w-[420px] mx-auto'
                : 'aspect-video'
          }`}
          data-testid="clip-player-container"
        >
          <video
            ref={videoRef}
            className={`w-full h-full ${verticalMode ? 'object-cover' : 'object-contain'}`}
            playsInline
            onClick={togglePlay}
          />

          {/* Cover XbotGo burn-in watermark until a sponsorship deal is in place */}
          <XbotCoverOverlay />

          {/* Back Button - Always visible in fullscreen */}
          {isFullscreen && (
            <button
              onClick={handleBack}
              className="absolute top-4 left-4 z-50 flex items-center gap-2 px-3 py-2 bg-black/60 hover:bg-black/80 rounded-lg text-white transition-colors"
              data-testid="fullscreen-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Back</span>
            </button>
          )}

          {/* Exit Fullscreen Button */}
          {isFullscreen && (
            <button
              onClick={exitFullscreen}
              className="absolute top-4 right-4 z-50 p-2 bg-black/60 hover:bg-black/80 rounded-lg text-white transition-colors"
              data-testid="exit-fullscreen-btn"
            >
              <X className="w-5 h-5" />
            </button>
          )}

          {/* Play/Pause Overlay */}
          {!playing && (
            <button
              onClick={togglePlay}
              className="absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity"
              data-testid="clip-play-overlay"
            >
              <div className="w-20 h-20 rounded-full bg-red-600/90 flex items-center justify-center hover:bg-red-500 transition-colors">
                <Play className="w-10 h-10 text-white ml-1" />
              </div>
            </button>
          )}

          {/* Clip Badge */}
          <div className={`absolute ${isFullscreen ? 'top-16' : 'top-3'} left-3`}>
            <Badge className="bg-red-600 text-white text-xs font-bold px-2 py-1">
              <Clock className="w-3 h-3 mr-1" />
              {(() => {
                const d = clip.duration ?? clip.duration_seconds;
                return d ? `${Math.round(d)}s clip` : 'Highlight';
              })()}
            </Badge>
          </div>

          {/* Rooam Watermark */}
          <div className={`absolute ${isFullscreen ? 'top-16' : 'top-3'} right-3 opacity-60`}>
            <span className="text-white text-xs font-bold tracking-widest">ROOAM</span>
          </div>

          {/* Video Controls */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-4">
            {/* Progress Bar */}
            <div className="relative h-1 bg-white/30 rounded-full mb-3">
              <div 
                className="absolute h-full bg-red-500 rounded-full transition-all"
                style={{ 
                  width: clip ? `${((currentTime - clip.start_time) / (clip.end_time - clip.start_time)) * 100}%` : '0%'
                }}
              />
            </div>

            {/* Control Buttons */}
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={skipBack}
                className="p-2 text-white/80 hover:text-white transition-colors"
                title="Skip back 5s"
                data-testid="clip-skip-back"
              >
                <SkipBack className="w-5 h-5" />
              </button>

              <button
                onClick={togglePlay}
                className="p-3 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                data-testid="clip-play-pause"
              >
                {playing ? <Pause className="w-6 h-6 text-white" /> : <Play className="w-6 h-6 text-white ml-0.5" />}
              </button>

              <button
                onClick={skipForward}
                className="p-2 text-white/80 hover:text-white transition-colors"
                title="Skip forward 5s"
                data-testid="clip-skip-forward"
              >
                <SkipForward className="w-5 h-5" />
              </button>

              <button
                onClick={replayClip}
                className="p-2 text-white/80 hover:text-white transition-colors"
                title="Replay clip"
                data-testid="clip-replay"
              >
                <RotateCcw className="w-5 h-5" />
              </button>

              <button
                onClick={toggleFullscreen}
                className="p-2 text-white/80 hover:text-white transition-colors ml-2"
                title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
                data-testid="clip-fullscreen"
              >
                {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Clip Info */}
        <div className="px-4 py-5 max-w-4xl mx-auto">
          <h1 className="text-xl sm:text-2xl font-bold text-white mb-1" data-testid="clip-title">
            {clip.title}
          </h1>
          <div className="flex flex-wrap items-center gap-3 text-sm text-zinc-500 mb-4">
            {(clip.home_team || clip.away_team) && (
              <>
                <span>{clip.home_team || '—'} vs {clip.away_team || '—'}</span>
                <span className="text-zinc-700">|</span>
              </>
            )}
            {clip.start_time != null && clip.end_time != null && (
              <>
                <span className="font-mono">{formatTime(clip.start_time)} - {formatTime(clip.end_time)}</span>
              </>
            )}
            {clip.views > 0 && (
              <>
                <span className="text-zinc-700">|</span>
                <span><Eye className="w-3 h-3 inline mr-0.5" />{clip.views} views</span>
              </>
            )}
          </div>

          {clip.description && (
            <p className="text-sm text-zinc-400 mb-4">{clip.description}</p>
          )}

          {/* Tagged Player */}
          {clip.player_name && (
            <div className="mb-4" data-testid="tagged-player-section">
              {taggedAthlete ? (
                <Link
                  to={`/athlete/${taggedAthlete.id}`}
                  className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 rounded-full px-3 py-1.5 transition-colors"
                  data-testid="tagged-player-link"
                >
                  <img
                    src={taggedAthlete.avatar ? (taggedAthlete.avatar.startsWith('http') ? taggedAthlete.avatar : `${API_URL}${taggedAthlete.avatar}`) : `https://api.dicebear.com/7.x/avataaars/svg?seed=${taggedAthlete.id}`}
                    alt={taggedAthlete.name}
                    className="w-5 h-5 rounded-full object-cover"
                  />
                  <span className="text-sm text-white font-medium">{taggedAthlete.name}</span>
                  {taggedAthlete.number > 0 && (
                    <span className="text-xs text-zinc-500">#{taggedAthlete.number}</span>
                  )}
                </Link>
              ) : (
                <div className="inline-flex items-center gap-2 bg-zinc-900 border border-zinc-700 rounded-full px-3 py-1.5">
                  <User className="w-4 h-4 text-zinc-400" />
                  <span className="text-sm text-zinc-300">{clip.player_name}</span>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3 flex-wrap">
            <Button
              onClick={handleLike}
              variant={liked ? "default" : "outline"}
              size="sm"
              className={liked 
                ? "bg-red-600 text-white" 
                : "border-zinc-700 text-zinc-300 hover:text-white"
              }
              data-testid="clip-like-btn"
            >
              <Heart className={`w-4 h-4 mr-1.5 ${liked ? 'fill-white' : ''}`} />
              {clip.likes || 0}
            </Button>

            <Button
              onClick={handleShare}
              variant="outline"
              size="sm"
              className="border-zinc-700 text-zinc-300 hover:text-white"
              data-testid="clip-share-btn"
            >
              {copied ? <Check className="w-4 h-4 mr-1.5 text-green-500" /> : <Share2 className="w-4 h-4 mr-1.5" />}
              Share
            </Button>

            <Button
              onClick={handleDownload}
              variant="outline"
              size="sm"
              disabled={downloading}
              className="border-zinc-700 text-zinc-300 hover:text-white"
              data-testid="clip-download-btn"
            >
              {downloading ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-1.5" />
              )}
              Download
            </Button>

            <Button
              onClick={() => setVerticalMode((v) => !v)}
              variant="outline"
              size="sm"
              className="border-zinc-700 text-zinc-300 hover:text-white"
              data-testid="clip-vertical-toggle"
              title={verticalMode ? 'Switch to 16:9 widescreen' : 'Switch to 9:16 for Stories / TikTok'}
            >
              {verticalMode ? (
                <>
                  <Monitor className="w-4 h-4 mr-1.5" /> 16:9
                </>
              ) : (
                <>
                  <Smartphone className="w-4 h-4 mr-1.5" /> 9:16
                </>
              )}
            </Button>

            <Button
              asChild
              variant="outline"
              size="sm"
              className="border-zinc-700 text-zinc-300 hover:text-white ml-auto"
            >
              <Link to={`/game-live/${clip.game_id}`}>
                Watch Full Game <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
      
      {/* Spacer for footer */}
      <div className="h-16" />
    </div>
  );
}
